import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import { API_BASE } from "../utils/api";
const API_URL = API_BASE;

export default function Register() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    company_name: "",
    password: "",
    confirm_password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) {
      setError("Passwords do not match.");
      return;
    }
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_URL}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: form.full_name,
          email: form.email,
          password: form.password,
          company_name: form.company_name || null,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.detail || "Registration failed. Please try again.");
        return;
      }

      login(data.access_token, data.user);
      navigate("/dashboard");
    } catch (err) {
      setError("Could not connect to server. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const EyeIcon = ({ show }) => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      {show ? (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
      ) : (
        <>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </>
      )}
    </svg>
  );

  const isDisabled = !form.full_name || !form.email || !form.password || !form.confirm_password || loading;

  const inputClass = "w-full px-4 py-3 border border-luxury-border rounded-xl focus:outline-none focus:ring-2 focus:ring-luxury-gold/40 focus:border-luxury-gold/60 bg-luxury-surface text-luxury-text text-sm placeholder-luxury-muted transition";

  return (
    <div className="min-h-screen flex bg-luxury-bg">

      {/* Left panel — dark branding */}
      <div className="hidden lg:flex lg:w-5/12 xl:w-1/2 flex-col justify-between bg-luxury-surface px-12 py-14 relative overflow-hidden border-r border-luxury-border">

        {/* Logo */}
        <div className="relative flex items-center gap-3">
          <div className="w-11 h-11 bg-luxury-elevated border border-luxury-gold/40 rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-luxury-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <span className="text-luxury-text font-bold text-lg leading-none">TaxVault</span>
            <p className="text-luxury-muted text-xs">South Africa</p>
          </div>
        </div>

        {/* Main copy */}
        <div className="relative space-y-6">
          <div>
            <h2 className="text-3xl xl:text-4xl font-bold text-luxury-text leading-snug">
              Start your tax journey<br />
              <span className="text-luxury-gold">the smart way.</span>
            </h2>
            <p className="text-luxury-secondary mt-3 text-sm leading-relaxed">
              Join thousands of South African businesses managing their tax documents securely in one place.
            </p>
          </div>

          <div className="space-y-4">
            {[
              { title: "Bank-level Security", desc: "AES-256 encrypted document storage" },
              { title: "Organised Vault", desc: "Auto-categorised by tax type and year" },
              { title: "SARS Compliant", desc: "Built for South African tax requirements" },
              { title: "Instant Access", desc: "Retrieve any document in seconds" },
            ].map((feat) => (
              <div key={feat.title} className="flex items-start gap-3">
                <div className="w-2 h-2 rounded-full bg-luxury-gold mt-1.5 flex-shrink-0" />
                <div>
                  <p className="text-luxury-text font-semibold text-sm">{feat.title}</p>
                  <p className="text-luxury-muted text-xs">{feat.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer note */}
        <div className="relative">
          <p className="text-luxury-muted text-xs">
            © 2026 TaxVault — Proudly South African
          </p>
        </div>
      </div>

      {/* Right panel — register form */}
      <div className="flex-1 flex flex-col justify-center px-6 py-10 sm:px-10 lg:px-14 xl:px-20 bg-luxury-bg">

        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <div className="w-9 h-9 bg-luxury-elevated border border-luxury-gold/40 rounded-xl flex items-center justify-center">
            <svg className="w-5 h-5 text-luxury-gold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <span className="font-bold text-luxury-text">TaxVault</span>
        </div>

        <div className="max-w-md w-full mx-auto">
          <div className="mb-7">
            <h1 className="text-2xl font-bold text-luxury-text">Create your account</h1>
            <p className="text-luxury-secondary text-sm mt-1">Free to start — no credit card required</p>
          </div>

          {error && (
            <div className="flex items-center gap-2.5 bg-red-900/20 border border-red-800/50 text-red-400 px-4 py-3 rounded-xl mb-5 text-sm">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Full Name */}
            <div>
              <label className="block text-sm font-semibold text-luxury-secondary mb-1.5">Full Name</label>
              <input type="text" name="full_name" value={form.full_name}
                onChange={handleChange} required autoComplete="name" placeholder="John Smith"
                className={inputClass} />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-luxury-secondary mb-1.5">Email Address</label>
              <input type="email" name="email" value={form.email}
                onChange={handleChange} required autoComplete="email" placeholder="you@company.com"
                className={inputClass} />
            </div>

            {/* Company (optional) */}
            <div>
              <label className="block text-sm font-semibold text-luxury-secondary mb-1.5">
                Company Name
                <span className="ml-1.5 text-xs font-normal text-luxury-muted">(optional)</span>
              </label>
              <input type="text" name="company_name" value={form.company_name}
                onChange={handleChange} placeholder="Your Company Pty Ltd"
                className={inputClass} />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-luxury-secondary mb-1.5">Password</label>
              <div className="relative">
                <input type={showPass ? "text" : "password"} name="password" value={form.password}
                  onChange={handleChange} required autoComplete="new-password" placeholder="Min. 6 characters"
                  className={inputClass + " pr-12"} />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-luxury-muted hover:text-luxury-secondary transition">
                  <EyeIcon show={showPass} />
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-semibold text-luxury-secondary mb-1.5">Confirm Password</label>
              <div className="relative">
                <input type={showConfirm ? "text" : "password"} name="confirm_password" value={form.confirm_password}
                  onChange={handleChange} required autoComplete="new-password" placeholder="Repeat your password"
                  className={inputClass + " pr-12"} />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-luxury-muted hover:text-luxury-secondary transition">
                  <EyeIcon show={showConfirm} />
                </button>
              </div>
              {form.password && form.confirm_password && form.password !== form.confirm_password && (
                <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
              )}
            </div>

            {/* Submit */}
            <button type="submit" disabled={isDisabled}
              className={`w-full py-3 rounded-xl font-bold text-sm transition-all duration-200
                ${isDisabled
                  ? "bg-luxury-elevated text-luxury-muted cursor-not-allowed border border-luxury-border"
                  : "bg-luxury-cta hover:bg-luxury-cta-hover text-white active:scale-95"
                }`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Creating your account...
                </span>
              ) : "Create Account →"}
            </button>
          </form>

          {/* Sign in link */}
          <p className="text-center text-sm text-luxury-muted mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-luxury-gold hover:text-luxury-gold-dim font-semibold transition-colors">Sign in</Link>
          </p>

          {/* Trust note */}
          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-luxury-muted">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span>Your data is encrypted and secure</span>
          </div>
        </div>
      </div>
    </div>
  );
}
